import React from 'react';
import { createPortal } from 'react-dom';
import { styled } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Colors } from 'library';

type ModalPadding = 'none' | 'default';

interface ModalProps {
    title?: string;
    subtitle?: string;
    headerBar?: React.ReactNode;
    onClose: () => void;
    children: React.ReactNode;
    width?: number;
    maxHeight?: string;
    dismissible?: boolean;
    padding?: ModalPadding;
    id?: string;
}

export function Modal({
    title,
    subtitle,
    headerBar,
    onClose,
    children,
    width = 520,
    maxHeight,
    dismissible = true,
    padding = 'default',
    id,
}: ModalProps) {
    return createPortal(
        <Overlay onClick={dismissible ? onClose : undefined}>
            <Content onClick={(e) => e.stopPropagation()} style={{ width, maxHeight }} id={id}>
                {headerBar && <HeaderBar>{headerBar}</HeaderBar>}
                {title && (
                    <Header>
                        <Title>{title}</Title>
                        {subtitle && <Subtitle>{subtitle}</Subtitle>}
                    </Header>
                )}
                {dismissible && (
                    <CloseButton onClick={onClose}>
                        <Close sx={{ fontSize: 20, color: Colors.textSecondary }} />
                    </CloseButton>
                )}
                <Body $hasTitle={Boolean(title)} $padding={padding}>
                    {children}
                </Body>
            </Content>
        </Overlay>,
        document.body,
    );
}

const Overlay = styled('div')({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowY: 'auto',
    zIndex: 1300,
});

const Content = styled('div')({
    position: 'relative',
    backgroundColor: Colors.white,
    borderRadius: 12,
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
});

const HeaderBar = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 0',
    borderBottom: `1px solid ${Colors.border}`,
    flexShrink: 0,
});

const Header = styled('div')({
    backgroundColor: Colors.white,
    padding: '24px 32px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexDirection: 'column',
    flexShrink: 0,
});

const Body = styled('div')<{ $hasTitle: boolean; $padding: ModalPadding }>(({ $hasTitle, $padding }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: $padding === 'none' ? 0 : 24,
    padding: $padding === 'none' ? 0 : $hasTitle ? '0 32px 28px' : '28px 32px',
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
}));

const Title = styled('h2')({
    fontSize: 20,
    fontWeight: 700,
    color: Colors.primary,
    margin: 0,
});

const Subtitle = styled('span')({
    fontSize: 14,
    color: Colors.secondary,
    fontWeight: 500,
});

export const ModalFooter = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
});

const CloseButton = styled('button')({
    position: 'absolute',
    top: 12,
    right: 12,
    border: 'none',
    background: 'none',
    borderRadius: 8,
    padding: 4,
    cursor: 'pointer',
    zIndex: 1,
    '&:hover': {
        backgroundColor: Colors.fill,
    },
});
